import React, { useState, useEffect } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import {
  fetchMyProfile, updateMyProfile, changeMyPassword,
  getCurrentUser, getTenantSlug, buildStaffPortalUrl,
} from '../../services/api.js';
import { Card, Btn, Field, Input, Select, Avatar, Toast, Spinner } from '../../components/ui.jsx';

const ROLE_LABELS = {
  TENANT_ADMIN:  'Tenant Admin',
  PROPERTY_ADMIN:'Property Admin',
  FRONT_DESK:    'Front Desk',
  HOUSEKEEPING:  'Housekeeping',
  MANAGER:       'Manager',
  ACCOUNTANT:    'Accountant',
  SUPERVISOR:    'Supervisor',
};
const roleLabel = r => ROLE_LABELS[r] || (r ? r.replace(/_/g, ' ') : 'Staff');
const isAdminRole = r => ['TENANT_ADMIN', 'PROPERTY_ADMIN', 'MANAGER'].includes(r);

const ROLE_COLORS = {
  TENANT_ADMIN: T.gold,
  PROPERTY_ADMIN: T.gold,
  MANAGER: T.purple || '#7C3AED',
  FRONT_DESK: T.teal,
  HOUSEKEEPING: T.success,
  ACCOUNTANT: '#5C8EE6',
  SUPERVISOR: T.warning,
};

function StatBox({ icon, label, value, color }) {
  return (
    <div style={{ textAlign: 'center', padding: '16px 12px', background: T.surface,
      borderRadius: 12, border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
      <p style={{ fontFamily: FONTS.display, fontSize: 20, fontWeight: 700,
        color: color || T.navy, lineHeight: 1 }}>{value}</p>
      <p style={{ fontSize: 10, color: T.muted, marginTop: 4, textTransform: 'uppercase',
        letterSpacing: .6, fontWeight: 600 }}>{label}</p>
    </div>
  );
}

function PortalLinkCard({ tenantSlug }) {
  const [copied, setCopied] = useState(false);
  const url = buildStaffPortalUrl(tenantSlug);
  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(url); }
    catch {
      const el = document.createElement('textarea');
      el.value = url; document.body.appendChild(el);
      el.select(); document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };
  return (
    <div style={{ background: T.tealPale, border: `1px solid ${T.teal}30`,
      borderRadius: 12, padding: '16px 18px' }}>
      <p style={{ fontSize: 12, fontWeight: 700, color: T.teal, marginBottom: 8 }}>
        📎 Your Hotel Login Link
      </p>
      <p style={{ fontSize: 11, color: T.slate, lineHeight: 1.5, marginBottom: 12 }}>
        Bookmark this link to access the system directly — no need to remember tenant slugs.
      </p>
      <div style={{ background: T.white, border: `1px solid ${T.teal}30`, borderRadius: 8,
        padding: '8px 12px', fontSize: 11, fontFamily: FONTS.mono, color: T.navy,
        wordBreak: 'break-all', marginBottom: 10 }}>{url}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={copyUrl} style={{
          flex: 1, background: copied ? T.successPale : T.white,
          border: `1px solid ${copied ? T.success : T.teal}40`,
          borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
          fontSize: 12, color: copied ? T.success : T.teal,
          fontWeight: 700, fontFamily: FONTS.body, transition: 'all .2s',
        }}>
          {copied ? '✅ Copied!' : '📋 Copy Link'}
        </button>
        <a href={url} target="_blank" rel="noreferrer" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: 8, padding: '8px 14px', fontSize: 12,
          color: T.slate, textDecoration: 'none', fontFamily: FONTS.body,
        }}>🔗 Open</a>
      </div>
    </div>
  );
}

function PasswordStrength({ pwd }) {
  if (!pwd) return null;
  const checks = [pwd.length >= 8, /[A-Z]/.test(pwd), /[0-9]/.test(pwd), /[^A-Za-z0-9]/.test(pwd)];
  const score = checks.filter(Boolean).length;
  const labels = ['Weak', 'Fair', 'Good', 'Strong'];
  const colors = [T.danger, T.warning, T.teal, T.success];
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 2,
            background: i < score ? colors[score - 1] : T.border, transition: 'background .3s' }} />
        ))}
      </div>
      <p style={{ fontSize: 10, color: score > 0 ? colors[score - 1] : T.muted, fontWeight: 600 }}>
        {score > 0 ? labels[score - 1] : ''}
        {score < 4 && score > 0 && ' — '}
        {score === 0 ? '' : score < 4 ? checks.map((c, i) => !c ? [' min 8 chars', ' uppercase', ' number', ' symbol'][i] : null).filter(Boolean)[0] : ' Password is strong ✓'}
      </p>
    </div>
  );
}

export default function ProfilePage({ onLogout }) {
  const [profile, setProfile]  = useState(null);
  const [loading, setLoading]  = useState(true);
  const [saving,  setSaving]   = useState(false);
  const [toast,   setToast]    = useState('');
  const [tab,     setTab]      = useState('profile');
  const [pwd, setPwd]          = useState({ current: '', newPwd: '', confirm: '' });
  const [showPwd,  setShowPwd] = useState({ current: false, newPwd: false, confirm: false });

  const jwtUser    = getCurrentUser();
  const tenantSlug = getTenantSlug();
  const showToast  = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  useEffect(() => {
    fetchMyProfile()
      .then(data => {
        setProfile({
          firstName: data.firstName || jwtUser?.firstName || '',
          lastName:  data.lastName  || jwtUser?.lastName  || '',
          email:     data.email     || jwtUser?.email     || jwtUser?.sub || '',
          phone:     data.phone     || '',
          role:      data.role      || jwtUser?.role      || jwtUser?.roles?.[0] || 'FRONT_DESK',
          shift:     data.shift     || 'morning',
          staffId:   data.id        || jwtUser?.staffId   || jwtUser?.sub,
          department: data.department || '',
          bio:       data.bio || '',
          ...data,
        });
      })
      .catch(() => {
        if (jwtUser) {
          setProfile({
            firstName: jwtUser.firstName || jwtUser.given_name  || '',
            lastName:  jwtUser.lastName  || jwtUser.family_name || '',
            email:     jwtUser.email     || jwtUser.sub         || '',
            phone: '', role: jwtUser.role || jwtUser.roles?.[0] || 'FRONT_DESK',
            shift: 'morning', staffId: jwtUser.staffId || jwtUser.sub,
            department: '', bio: '',
          });
        } else { setProfile(null); }
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateMyProfile({
        firstName: profile.firstName, lastName: profile.lastName,
        email: profile.email, phone: profile.phone, shift: profile.shift,
        department: profile.department, bio: profile.bio,
      });
      setProfile(p => ({ ...p, ...updated }));
      showToast('✅ Profile updated successfully');
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async () => {
    if (pwd.newPwd !== pwd.confirm) { showToast('Passwords do not match'); return; }
    if (pwd.newPwd.length < 6) { showToast('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await changeMyPassword({ currentPassword: pwd.current, newPassword: pwd.newPwd });
      showToast('✅ Password changed successfully');
      setPwd({ current: '', newPwd: '', confirm: '' });
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <Spinner />
    </div>
  );

  if (!profile) return (
    <div style={{ maxWidth: 480, margin: '60px auto', textAlign: 'center', padding: '0 20px' }}>
      <p style={{ fontSize: 36, marginBottom: 12 }}>⚠️</p>
      <p style={{ fontSize: 15, fontWeight: 700, color: T.danger }}>Could not load profile</p>
      <p style={{ fontSize: 13, color: T.muted, marginTop: 8 }}>
        Your session may have expired. Please log in again.
      </p>
      <div style={{ marginTop: 20 }}>
        <Btn variant="danger" onClick={onLogout}>🚪 Log Out</Btn>
      </div>
    </div>
  );

  const displayName = `${profile.firstName} ${profile.lastName}`.trim() || profile.email;
  const isAdmin     = isAdminRole(profile.role);
  const roleColor   = ROLE_COLORS[profile.role] || T.teal;
  const authMode    = window !== window.parent ? 'Main Hall (iframe)' : 'Standalone';

  const isDesktop = !!window.pms;

  const TABS = [
    { id: 'profile',  icon: '👤', label: 'Edit Profile' },
    { id: 'security', icon: '🔐', label: 'Security' },
    { id: 'access',   icon: '🔗', label: 'Access Link', desktopHide: true },
    { id: 'session',  icon: '📋', label: 'Session Info' },
  ].filter(t => !(isDesktop && t.desktopHide));

  return (
    <div className="fade-up">
      <style>{`
        .profile-tab-btn { transition: all .15s; }
        .profile-tab-btn:hover { background: ${T.surface} !important; }
        @media(max-width: 768px) { .profile-grid { grid-template-columns: 1fr !important; } }
      `}</style>

      {/* Hero Banner */}
      <div style={{
        background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyLight} 60%, ${roleColor}22 100%)`,
        borderRadius: 20, padding: '28px 32px', marginBottom: 20, color: T.white,
        display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
        boxShadow: SHADOW.lg, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200,
          borderRadius: '50%', background: `${roleColor}10`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -60, right: 80, width: 160, height: 160,
          borderRadius: '50%', background: `${T.teal}08`, pointerEvents: 'none' }} />
        {/* Avatar */}
        <div style={{ width: 80, height: 80, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${roleColor}, ${T.teal})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, fontWeight: 800, color: T.white,
          boxShadow: `0 0 0 4px ${T.white}20, 0 8px 24px rgba(0,0,0,.3)` }}>
          {displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'S'}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h1 style={{ fontFamily: FONTS.display, fontSize: 'clamp(20px,3vw,28px)', fontWeight: 700,
              color: T.white, lineHeight: 1, margin: 0 }}>{displayName}</h1>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
              background: `${roleColor}30`, color: roleColor, border: `1px solid ${roleColor}40`,
              textTransform: 'uppercase', letterSpacing: .5 }}>{roleLabel(profile.role)}</span>
            {isAdmin && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
                background: `${T.gold}25`, color: T.gold, border: `1px solid ${T.gold}40` }}>
                ⭐ ADMIN
              </span>
            )}
          </div>
          <p style={{ fontSize: 13, color: `${T.white}80`, margin: 0 }}>{profile.email}</p>
          {profile.department && (
            <p style={{ fontSize: 11, color: `${T.white}60`, margin: '4px 0 0' }}>🏢 {profile.department}</p>
          )}
          <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
            {profile.phone && <span style={{ fontSize: 11, color: `${T.white}70` }}>📞 {profile.phone}</span>}
            <span style={{ fontSize: 11, color: `${T.white}70` }}>🏨 {tenantSlug}</span>
            <span style={{ fontSize: 11, color: `${T.white}70` }}>⏰ {profile.shift} shift</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <Btn variant="gold" onClick={() => setTab('profile')}>✏️ Edit Profile</Btn>
          <button onClick={onLogout} style={{
            background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.2)',
            borderRadius: 10, padding: '9px 16px', cursor: 'pointer', fontSize: 12,
            color: T.white, fontFamily: FONTS.body, fontWeight: 600,
          }}>🚪 Log Out</button>
        </div>
      </div>

      {/* Quick stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <StatBox icon="🎯" label="Role" value={roleLabel(profile.role).split(' ')[0]} color={roleColor} />
        <StatBox icon="⏰" label="Shift" value={profile.shift?.charAt(0).toUpperCase() + profile.shift?.slice(1) || '—'} color={T.teal} />
        <StatBox icon="🏨" label="Tenant" value={tenantSlug || '—'} color={T.navy} />
        <StatBox icon="🔒" label="Auth Mode" value={authMode === 'Standalone' ? 'Direct' : 'Main Hall'} color={T.success} />
      </div>

      <div className="profile-grid" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20 }}>
        {/* Left: Tab navigation */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Card style={{ padding: '14px 12px' }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase',
              letterSpacing: .7, marginBottom: 10, paddingLeft: 4 }}>Account Menu</p>
            {TABS.map(item => (
              <button key={item.id} className="profile-tab-btn" onClick={() => setTab(item.id)} style={{
                width: '100%', background: tab === item.id
                  ? `linear-gradient(135deg, ${T.teal}18, ${T.teal}10)`
                  : 'none',
                border: `1px solid ${tab === item.id ? `${T.teal}40` : 'transparent'}`,
                borderRadius: 10, padding: '10px 12px', cursor: 'pointer',
                fontFamily: FONTS.body, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 9,
                color: tab === item.id ? T.teal : T.slate,
                fontWeight: tab === item.id ? 700 : 400, marginBottom: 2, fontSize: 13,
                transition: 'all .15s',
              }}>
                <span style={{ fontSize: 16 }}>{item.icon}</span>
                {item.label}
              </button>
            ))}
            <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 10, paddingTop: 10 }}>
              <button onClick={onLogout} style={{
                width: '100%', background: 'none', border: `1px solid ${T.danger}25`,
                borderRadius: 10, padding: '10px 12px', cursor: 'pointer',
                fontFamily: FONTS.body, textAlign: 'left', fontSize: 13,
                color: T.danger, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 9,
                transition: 'all .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.dangerPale}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <span style={{ fontSize: 16 }}>🚪</span> Log Out
              </button>
            </div>
          </Card>

          {/* Staff ID card */}
          <Card style={{ padding: '14px 16px' }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase',
              letterSpacing: .7, marginBottom: 10 }}>Staff ID</p>
            <p style={{ fontSize: 11, color: T.navy, fontFamily: FONTS.mono, wordBreak: 'break-all',
              background: T.surface, borderRadius: 8, padding: '8px 10px', border: `1px solid ${T.border}` }}>
              {profile.staffId || jwtUser?.sub || '—'}
            </p>
            <p style={{ fontSize: 10, color: T.muted, marginTop: 8 }}>
              Module: <strong>Reservation</strong>
            </p>
          </Card>
        </div>

        {/* Right: Tab content */}
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          {/* Tab header */}
          <div style={{ padding: '18px 24px', borderBottom: `1px solid ${T.border}`,
            background: `linear-gradient(135deg, ${T.surface}, ${T.white})` }}>
            <p style={{ fontSize: 16, fontWeight: 700, color: T.navy, margin: 0 }}>
              {TABS.find(t => t.id === tab)?.icon} {TABS.find(t => t.id === tab)?.label}
            </p>
          </div>

          <div style={{ padding: '24px' }}>
            {/* ── Edit Profile ── */}
            {tab === 'profile' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <Field label="First Name" required>
                    <Input value={profile.firstName || ''} onChange={v => setProfile(p => ({ ...p, firstName: v }))} placeholder="First name" />
                  </Field>
                  <Field label="Last Name" required>
                    <Input value={profile.lastName || ''} onChange={v => setProfile(p => ({ ...p, lastName: v }))} placeholder="Last name" />
                  </Field>
                  <Field label="Email" required={!isAdmin}>
                    <Input type="email" value={profile.email || ''}
                      onChange={v => setProfile(p => ({ ...p, email: v }))} disabled={isAdmin}
                      placeholder="staff@hotel.com" />
                    {isAdmin && <p style={{ fontSize: 10, color: T.muted, marginTop: 3 }}>Managed in Main Hall</p>}
                  </Field>
                  <Field label="Phone">
                    <Input value={profile.phone || ''} onChange={v => setProfile(p => ({ ...p, phone: v }))} placeholder="+250 7xx xxx xxx" />
                  </Field>
                  <Field label="Department">
                    <Input value={profile.department || ''} onChange={v => setProfile(p => ({ ...p, department: v }))} placeholder="e.g. Front Office, Finance" />
                  </Field>
                  <Field label="Preferred Shift">
                    <Select value={profile.shift || 'morning'} onChange={v => setProfile(p => ({ ...p, shift: v }))}>
                      {['morning', 'afternoon', 'evening', 'night', 'flexible'].map(s => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Role" hint="Contact admin to change">
                    <Input value={roleLabel(profile.role)} disabled />
                  </Field>
                </div>
                <Field label="Bio / Notes" style={{ marginTop: 14 }}>
                  <textarea value={profile.bio || ''} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
                    placeholder="Short bio or notes about your role…"
                    rows={3} style={{ width: '100%', border: `1.5px solid ${T.border}`, borderRadius: 10,
                      padding: '10px 13px', fontSize: 13, fontFamily: FONTS.body, color: T.navy,
                      outline: 'none', resize: 'vertical', lineHeight: 1.5, boxSizing: 'border-box' }} />
                </Field>
                <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
                  <Btn variant="primary" disabled={saving} onClick={handleSave}>
                    {saving ? '⏳ Saving…' : '💾 Save Changes'}
                  </Btn>
                </div>
              </>
            )}

            {/* ── Security ── */}
            {tab === 'security' && (
              <>
                {isAdmin && (
                  <div style={{ background: T.tealPale, border: `1px solid ${T.teal}30`,
                    borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
                    <p style={{ fontSize: 12, color: T.teal, margin: 0 }}>
                      ℹ️ As a Tenant Admin, you can also manage your password in <strong>Main Hall</strong>.
                    </p>
                  </div>
                )}
                <div style={{ maxWidth: 440 }}>
                  {[
                    { key: 'current', label: 'Current Password', placeholder: '••••••••' },
                    { key: 'newPwd',  label: 'New Password',     placeholder: 'Min. 6 characters' },
                    { key: 'confirm', label: 'Confirm Password', placeholder: 'Repeat new password' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key} style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: T.muted,
                        letterSpacing: .9, textTransform: 'uppercase', marginBottom: 5 }}>{label} <span style={{ color: T.danger }}>*</span></label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showPwd[key] ? 'text' : 'password'}
                          value={pwd[key]}
                          onChange={e => setPwd(p => ({ ...p, [key]: e.target.value }))}
                          placeholder={placeholder}
                          style={{ width: '100%', border: `1.5px solid ${T.border}`, borderRadius: 10,
                            padding: '10px 40px 10px 13px', fontSize: 13, fontFamily: FONTS.body,
                            color: T.navy, outline: 'none', boxSizing: 'border-box' }}
                        />
                        <button type="button" onClick={() => setShowPwd(p => ({ ...p, [key]: !p[key] }))}
                          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: T.muted }}>
                          {showPwd[key] ? '🙈' : '👁'}
                        </button>
                      </div>
                      {key === 'newPwd' && <PasswordStrength pwd={pwd.newPwd} />}
                      {key === 'confirm' && pwd.confirm && pwd.newPwd !== pwd.confirm && (
                        <p style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>⚠️ Passwords do not match</p>
                      )}
                    </div>
                  ))}
                  <Btn variant="primary"
                    disabled={saving || !pwd.current || !pwd.newPwd || pwd.newPwd !== pwd.confirm}
                    onClick={handlePasswordChange}>
                    {saving ? '⏳ Changing…' : '🔐 Change Password'}
                  </Btn>
                </div>
              </>
            )}

            {/* ── Access Link ── */}
            {tab === 'access' && (
              <>
                <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.6, marginBottom: 20 }}>
                  This link opens the reservation system pre-configured for <strong>{tenantSlug}</strong>.
                  Bookmark it or share with colleagues — it does <strong>not</strong> contain your password.
                </p>
                <PortalLinkCard tenantSlug={tenantSlug} />
                {isAdmin && (
                  <div style={{ marginTop: 16, background: T.warningPale, border: `1px solid ${T.warning}30`,
                    borderRadius: 12, padding: '12px 16px' }}>
                    <p style={{ fontSize: 12, color: '#B07D20', margin: 0, lineHeight: 1.6 }}>
                      💡 <strong>Admin tip:</strong> Share this link with all your staff so they always reach
                      the correct hotel portal.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* ── Session Info ── */}
            {tab === 'session' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    { label: 'Full Name',    value: displayName, icon: '👤' },
                    { label: 'Email',        value: profile.email || '—', icon: '📧' },
                    { label: 'Role',         value: roleLabel(profile.role), icon: '🎯' },
                    { label: 'Tenant',       value: tenantSlug, icon: '🏨' },
                    { label: 'Shift',        value: profile.shift || '—', icon: '⏰' },
                    { label: 'Department',   value: profile.department || '—', icon: '🏢' },
                    { label: 'Staff ID',     value: profile.staffId || jwtUser?.sub || '—', icon: '🆔' },
                    { label: 'Session',      value: '✅ Active', icon: '🔒' },
                    { label: 'Module',       value: 'Reservation', icon: '📦' },
                    { label: 'Auth Mode',    value: authMode, icon: '🔐' },
                    { label: 'Local Time',   value: new Date().toLocaleString('en-GB'), icon: '🕐' },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', padding: '11px 14px', background: T.surface, borderRadius: 10,
                      border: `1px solid ${T.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 15 }}>{item.icon}</span>
                        <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>{item.label}</span>
                      </div>
                      <span style={{ fontSize: 12, color: T.navy, fontFamily: FONTS.mono,
                        maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 18 }}>
                  <Btn variant="danger" onClick={onLogout}>🚪 Log Out of All Sessions</Btn>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      <Toast message={toast} />
    </div>
  );
}